<script setup lang="ts">
import { reactive } from 'vue'
import { LinkItem } from '@/components/shared'
import { ElIcon, IconNames } from '@/components/icons'
//import { useDownloadUtils } from '@/models'
import { config } from '@/config'

interface TProps {
  lcid: string
  homeData: any
}
const { homeData } = defineProps<TProps>()

const releasesState = reactive({
  // currentRelease: '',
  // releases: [] as any[],
  // downloadUrl: '',
  // sampleDataFileName: 'tenorlab-sample-data.json',
  // sampleDataUrl: '',
  showWhatIsNewDialog: false,
  codePackages: config.packageNames.map((x) => ({ name: x, framework: '', version: '' })),
})

const analytics = useAnalytics()
const { translate } = useLocalization()
const { defaultCheckoutConfig } = useCountryInfo()

const ulCss = `mt-2 flex flex-col gap-2`
const linkItemCss = `flex gap-2 items-center text-xl border rounded-md border-primary border-opacity-50 p-2 content-body opacity-85 hover:opacity-100 hover:text-primary hover:border-opacity-100`
const linkItemIconCss = `h-6 w-auto flex-none`
const linkItemIconHeight = 24

// const { downloadUrl } = useDownloadUtils()
// const onDownloadSampleDataClick = async () => {
//   console.warn('NOT IMPLEMENTED')
//   //analytics.track('click', 'download-sampledata')
//   //await downloadUrl(releasesState.sampleDataFileName, releasesState.sampleDataUrl)
// }

const fetchVersionFromPackageJson = async (
  codePackageName: string,
): Promise<{
  name: string
  framework: string
  version: string
}> => {
  const framework = codePackageName.replace('tenorlab/', '').split('-')[0]
  // i.e. https://raw.githubusercontent.com/tenorlab/react-dashboard/refs/heads/main/package.json
  const url = `https://raw.githubusercontent.com/${codePackageName}/refs/heads/main/package.json`
  try {
    const response = await fetch(url)
    if (!response.ok) {
      throw new Error(`Response status: ${response.status}`)
    }
    const json = await response.json()
    let version: string = json?.version as any
    // for the patch version only use one digit
    const arr = version.split('.')
    console.log('fetchVersionFromPackageJson', version)
    // @ts-ignore
    version = arr.length > 2 ? `${arr[0]}.${arr[1]}.${arr[2][0]}` : version
    return { name: codePackageName, framework: framework || '', version }
  } catch (err) {
    console.error(err)
    return { name: codePackageName, framework: framework || '', version: 'N/A' }
  }
}

onMounted(async () => {
  if (import.meta.client) {
    const results = await Promise.all(config.packageNames.map((x) => fetchVersionFromPackageJson(x)))
    releasesState.codePackages = results
  }
})
</script>

<template>
  <PageContainerComponent>
    <template #header>
      <PageHeaderComponent>
        {{ translate('pages.releases.title') }}
        <template #subheader>
          {{ translate('pages.releases.subtitle') }}
        </template>
      </PageHeaderComponent>
    </template>
    <div class="flex flex-col gap-8 xl:grid xl:grid-cols-3">
      <!-- npm -->
      <div class="max-w-xl">
        <h2 id="npm" class="mb-4 text-2xl font-semibold leading-7">
          {{ translate('pages.releases.npm') }}
        </h2>
        <ClientOnly>
          <ul role="list" :class="ulCss">
            <li
              v-for="codePackage in releasesState.codePackages"
              :id="codePackage.name"
              :key="codePackage.name"
            >
              <LinkItem
                :to="`https://www.npmjs.com/package/@${codePackage.name}`"
                target="_blank"
                :title="`NPM Link to @${codePackage.name}`"
                :overrideCssClass="linkItemCss"
                @clicked="analytics.track('click', `npm_${codePackage.name}_v${codePackage.version}`)"
              >
                <span>@{{ codePackage.name }}</span>
                <span v-show="codePackage.version">(v{{ codePackage.version }})</span>
              </LinkItem>
            </li>
          </ul>
        </ClientOnly>
      </div>

      <!-- sourceCode -->
      <div class="max-w-xl">
        <h2 id="online" class="mb-4 text-2xl font-semibold leading-7">
          {{ translate('pages.releases.sourceCode') }}
        </h2>
        <ClientOnly>
          <ul role="list" :class="ulCss">
            <li
              v-for="codePackage in releasesState.codePackages"
              :id="codePackage.name"
              :key="codePackage.name"
            >
              <LinkItem
                :to="`https://github.com/${codePackage.name}`"
                target="_blank"
                :title="`GitHub Link to @${codePackage.name}`"
                :overrideCssClass="linkItemCss"
                @clicked="analytics.track('click', `github_${codePackage.name}_v${codePackage.version}`)"
              >
                <ElIcon :name="IconNames.github" sizeCss="size-5" fill="none" stroke="currentColor" />
                <span>@{{ codePackage.name }}</span>
              </LinkItem>
            </li>
          </ul>
        </ClientOnly>
      </div>

      <!-- demos -->
      <div class="max-w-xl">
        <h2 id="demos" class="mb-4 text-2xl font-semibold leading-7">
          {{ homeData?.footer?.demos?.title }}
        </h2>
        <ul role="list" :class="ulCss">
          <li v-for="demolink in homeData?.footer?.demos?.links" :key="demolink.id">
            <LinkItem
              :to="demolink.url"
              rel="noopener"
              target="_blank"
              :title="demolink.title"
              :overrideCssClass="linkItemCss"
              @clicked="analytics.track('click', `releases_${demolink.id}`)"
            >
              <img
                :class="linkItemIconCss"
                :src="demolink.icon"
                :alt="demolink.iconAlt"
                :height="linkItemIconHeight"
              />
              <span>{{ demolink.name }}</span>
            </LinkItem>
          </li>
        </ul>
      </div>
    </div>

    <div class="mt-16 flex flex-col gap-8 xl:grid xl:grid-cols-2">
      <!-- buy license -->
      <div class="max-w-xl">
        <h2 id="buylicense" class="mb-4 text-2xl font-semibold leading-7">
          🚀 {{ translate('navigation.license') }}
        </h2>
        <div class="flex flex-col gap-y-6" @click="analytics.track('click', 'buylicense')">
          <div @click="analytics.track('click', 'buylicense')">
            <a
              :href="defaultCheckoutConfig.checkoutUrl"
              :title="defaultCheckoutConfig.checkoutVendor"
              target="_blank"
              class="bg-primary content-primary hover:bg-primary rounded-md px-3.5 py-2.5 text-sm font-semibold shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
            >
              {{ translate('navigation.buyLicense') }}
            </a>
          </div>
          <div class="flex flex-col gap-2">
            <p>
              You can have access to the <span class="font-bold">full Tenorlab Pro source code</span>
              (as in the pro demos)—including the dynamic sidebar, multi-theme engine, and dashboard building
              logic-is available when you buy the Tenorlab Pro license. (the code will be made available
              through private github repos, or if you prefer emailed as a zip file to you)
            </p>
            <p>
              It is designed to save developers weeks of work by providing a production-ready Vite +
              TypeScript architecture.
            </p>
            <p>
              Use of the core @tenorlab/react-dashboard or @tenorlab/vue-dashboard libraries is still free
              (they are open-source and have MIT license).
            </p>
          </div>
        </div>
      </div>
    </div>
  </PageContainerComponent>
</template>
